import type { ConnectionScope, UpdatePermissionsInput } from "@typedoc/contracts/connections";
import type { ForStep } from "@typedoc/contracts/processes";
import { contractRequest, type ContractCall } from "../contract-request";
import type { RegionalSession } from "../regional-request";
import { connectionsClient, type ConnectionsClient } from "../rpc/connections";

/**
 * A connections call for a session: the workspace's region and token, the session's bound, and
 * failures as `AppError`. The session is bound once, where the screens learn the workspace
 * (`useConnectionsScope`), so the calls below take it and nothing else about who is calling.
 */
export const connectionsRequest = <T>(
  session: RegionalSession,
  call: ContractCall<ConnectionsClient, T>,
) => contractRequest(connectionsClient, session.scope, session.options, call);

export const fetchCatalog = (session: RegionalSession) =>
  connectionsRequest(session, (c, workspaceId, call) =>
    c.catalog({ params: { workspaceId } }, call),
  );

export const fetchAppCategories = (session: RegionalSession, app: string) =>
  connectionsRequest(session, (c, workspaceId, call) =>
    c.appCategories({ params: { workspaceId, app } }, call),
  );

/**
 * Whether to read other members' personal connections too (owners and admins only, read-only). The
 * query string carries text, so the flag goes as "true" and is left out when off.
 */
export type MembersPersonalOption = { membersPersonal?: boolean | undefined };
export const membersPersonalQuery = (options: MembersPersonalOption | undefined) =>
  options?.membersPersonal ? { membersPersonal: "true" } : {};

export const fetchConnections = (session: RegionalSession, options?: MembersPersonalOption) =>
  connectionsRequest(session, (c, workspaceId, call) =>
    c.list({ params: { workspaceId }, query: membersPersonalQuery(options) }, call),
  );

export const fetchApprovals = (session: RegionalSession) =>
  connectionsRequest(session, (c, workspaceId, call) =>
    c.approvals({ params: { workspaceId } }, call),
  );

/** What a chat's approval will do, with its arguments: only its approver is answered. */
export const fetchApprovalCard = (session: RegionalSession, id: string) =>
  connectionsRequest(session, (c, workspaceId, call) =>
    c.approvalCard({ params: { workspaceId, approvalId: id } }, call),
  );

export const fetchConnection = (
  session: RegionalSession,
  id: string,
  options?: MembersPersonalOption,
) =>
  connectionsRequest(session, (c, workspaceId, call) =>
    c.detail(
      { params: { workspaceId, connectionId: id }, query: membersPersonalQuery(options) },
      call,
    ),
  );

export const fetchPermissions = (session: RegionalSession, id: string) =>
  connectionsRequest(session, (c, workspaceId, call) =>
    c.permissions({ params: { workspaceId, connectionId: id } }, call),
  );

export const fetchEvents = (session: RegionalSession, id: string) =>
  connectionsRequest(session, (c, workspaceId, call) =>
    c.events({ params: { workspaceId, connectionId: id } }, call),
  );

export const startAttempt = (
  session: RegionalSession,
  input: {
    app: string;
    scope: ConnectionScope;
    connectionId?: string | undefined;
    returnTo?: "desktop" | undefined;
    /** The step a sign-in starts from: its completion sets that use's account (plan Task 16). */
    forStep?: ForStep | undefined;
  },
) =>
  connectionsRequest(session, (c, workspaceId, call) =>
    c.startAttempt({ params: { workspaceId }, body: input }, call),
  );

export const pollAttempt = (session: RegionalSession, id: string) =>
  connectionsRequest(session, (c, workspaceId, call) =>
    c.attempt({ params: { workspaceId, attemptId: id } }, call),
  );

export const completeAttempt = (session: RegionalSession, id: string) =>
  connectionsRequest(session, (c, workspaceId, call) =>
    c.completeAttempt({ params: { workspaceId, attemptId: id } }, call),
  );

export const decideApproval = (
  session: RegionalSession,
  id: string,
  input: { decision: "approve" | "deny"; remember?: boolean | undefined },
) =>
  connectionsRequest(session, (c, workspaceId, call) =>
    c.decideApproval({ params: { workspaceId, approvalId: id }, body: input }, call),
  );

export const updateLabel = (
  session: RegionalSession,
  id: string,
  input: { revision: number; label: string },
) =>
  connectionsRequest(session, (c, workspaceId, call) =>
    c.setLabel({ params: { workspaceId, connectionId: id }, body: input }, call),
  );

export const updatePermissions = (
  session: RegionalSession,
  id: string,
  input: UpdatePermissionsInput,
) =>
  connectionsRequest(session, (c, workspaceId, call) =>
    c.setPermissions({ params: { workspaceId, connectionId: id }, body: input }, call),
  );

export const setConnectionAvailableInChat = (
  session: RegionalSession,
  id: string,
  input: { revision: number; availableInChat: boolean },
) =>
  connectionsRequest(session, (c, workspaceId, call) =>
    c.setAvailableInChat({ params: { workspaceId, connectionId: id }, body: input }, call),
  );

export const pauseConnection = (session: RegionalSession, id: string) =>
  connectionsRequest(session, (c, workspaceId, call) =>
    c.pause({ params: { workspaceId, connectionId: id } }, call),
  );

export const resumeConnection = (session: RegionalSession, id: string) =>
  connectionsRequest(session, (c, workspaceId, call) =>
    c.resume({ params: { workspaceId, connectionId: id } }, call),
  );

export const disconnectConnection = (session: RegionalSession, id: string) =>
  connectionsRequest(session, (c, workspaceId, call) =>
    c.disconnect({ params: { workspaceId, connectionId: id } }, call),
  );
